/** Presentation only: escape model text and label verified execution metadata. */
export default class ChatPresentation {
    private progressFollowing = new WeakMap<HTMLElement, { top: number; following: boolean }>();

    /** Reveal the initial stage card once, when a new question is submitted. */
    public scrollToProcessStart(container: HTMLElement | null) {
        if (!container) return;
        const entries = container.querySelectorAll<HTMLElement>('[data-chat-role]');
        const latest = entries.item(entries.length - 1);
        const process = latest?.querySelector<HTMLElement>('[aria-label="답변 생성 과정"]');
        if (!process) return;
        const current = process.querySelector<HTMLElement>('[aria-current="step"]') || process.querySelector<HTMLElement>('li');
        const start = process.getBoundingClientRect().top;
        const active = current?.getBoundingClientRect();
        // Retain the card heading where it fits; keep the active row visible in short panels.
        const target = active ? Math.max(start, Math.min(active.top, active.bottom - container.clientHeight + 24)) : start;
        container.scrollTop = Math.max(0, container.scrollTop + target
            - container.getBoundingClientRect().top - container.clientTop - 12);
        this.progressFollowing.set(container, { top: container.scrollTop, following: true });
    }

    /** Reveal the beginning of the final answer after streaming finishes. */
    public scrollToAnswerStart(container: HTMLElement | null, answer: HTMLElement | null) {
        if (!container || !answer || !container.contains(answer)) return;
        const content = answer.querySelector<HTMLElement>('[data-chat-answer]') || answer;
        container.scrollTop = Math.max(0, container.scrollTop + content.getBoundingClientRect().top
            - container.getBoundingClientRect().top - container.clientTop - 12);
        this.progressFollowing.delete(container);
    }

    public resultAnalysisStep(message: any = null): any {
        return this.isDataSearch(message)
            ? { id: 'answer', label: '조회 결과 LLM 분석·요약', detail: 'Data Research Agent가 반환한 전체 결과를 LLM이 확인해 조회 건수와 대표 항목을 요약합니다.' }
            : { id: 'answer', label: '근거 분석 및 질의 언어 답변 작성', detail: '확인한 근거를 바탕으로 답변을 작성합니다.' };
    }

    public initialProcessSteps(message: any = null): any[] {
        return [
            { id: 'context', label: '문맥 분석', status: 'running', detail: '질문의 문맥 분석을 요청했습니다.' },
            { id: 'intent', label: '의도 파악', status: 'pending', detail: '문맥 분석 결과를 기다리고 있습니다.' },
            { id: 'parameters', label: '파라미터 구분', status: 'pending', detail: '질문에 필요한 조회 조건을 구분합니다.' },
            { id: 'tools', label: 'Tool 실행', status: 'pending', detail: '서버 실행 상태를 기다리고 있습니다.' },
            { ...this.resultAnalysisStep(message), status: 'pending' }
        ];
    }

    /** One authenticated POST carries execution events, answer text and the final result. */
    public async fetchWithProgress(url: string, options: any, onProgress: (snapshot: any) => void): Promise<Response> {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        options.body.set('progress_id', Array.from(bytes, value => value.toString(16).padStart(2, '0')).join(''));
        options.body.set('stream', '1');
        const headers = new Headers(options.headers);
        headers.set('Accept', 'text/event-stream');
        const response = await fetch(url, { ...options, headers });
        if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream')) return response;
        if (!response.body) throw new Error('답변 연결이 끊겼습니다. 질문을 다시 시도해 주세요.');
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8', { fatal: true });
        let buffer = '';
        let fields: string[] = [];
        let terminal: Response | null = null;
        let done = false;
        const aborted = () => { void reader.cancel().catch(() => undefined); };
        const checkAbort = () => {
            if (options.signal?.aborted) throw new DOMException('요청을 중단했습니다.', 'AbortError');
        };
        const dispatch = () => {
            if (!fields.length) return;
            const payload = fields.join('\n');
            fields = [];
            let event: any;
            try { event = JSON.parse(payload); }
            catch (_) { throw new Error('답변 연결의 응답 형식이 올바르지 않습니다. 다시 시도해 주세요.'); }
            checkAbort();
            if (!event || typeof event !== 'object') throw new Error('답변 연결의 응답 형식이 올바르지 않습니다.');
            if (event.type === 'done') { done = true; return; }
            if (terminal) return;
            if (event.type === 'progress') onProgress(event.data || {});
            else if (event.type === 'answer_delta' && typeof event.content === 'string') onProgress({ answerDelta: event.content });
            else if (event.type === 'answer_reset') onProgress({ answerReset: true });
            else if (event.type === 'result') {
                if (!event.data || typeof event.data !== 'object' || Array.isArray(event.data)) {
                    throw new Error('완료된 답변을 확인하지 못했습니다. 다시 시도해 주세요.');
                }
                terminal = new Response(JSON.stringify({ code: 200, data: event.data }), { status: 200, headers: { 'Content-Type': 'application/json' } });
            } else if (event.type === 'error') {
                const status = Number.isInteger(event.status) && event.status >= 400 && event.status <= 599 ? event.status : 500;
                const message = typeof event.message === 'string' ? event.message : '답변을 완료하지 못했습니다.';
                terminal = new Response(JSON.stringify({ code: status, data: { message, error: { code: event.code || 'REQUEST_FAILED', message, details: event.details || {} } } }), { status, headers: { 'Content-Type': 'application/json' } });
                // A claimed request must never be automatically replayed after a stream error.
                onProgress({ answerReset: true, streamFailed: true });
            }
        };
        const consume = (flush: boolean) => {
            let end: number;
            while ((end = buffer.search(/[\r\n]/)) >= 0) {
                // Wait for a possible LF in a CRLF delimiter split across chunks.
                if (!flush && buffer[end] === '\r' && end === buffer.length - 1) break;
                const line = buffer.slice(0, end);
                const count = buffer[end] === '\r' && buffer[end + 1] === '\n' ? 2 : 1;
                buffer = buffer.slice(end + count);
                if (!line) dispatch();
                else if (line.startsWith('data:')) fields.push(line.slice(5).replace(/^ /, ''));
                if (done) break;
            }
            if (buffer.length > 8 * 1024 * 1024) throw new Error('답변의 응답 크기를 처리하지 못했습니다. 질문 범위를 줄여 다시 시도해 주세요.');
            if (flush && !done) {
                if (buffer.startsWith('data:')) fields.push(buffer.slice(5).replace(/^ /, ''));
                buffer = '';
                dispatch();
            }
        };
        options.signal?.addEventListener('abort', aborted, { once: true });
        try {
            checkAbort();
            while (!done) {
                const chunk = await reader.read();
                checkAbort();
                buffer += decoder.decode(chunk.value, { stream: !chunk.done });
                consume(chunk.done);
                if (chunk.done) break;
            }
            if (!terminal) throw new Error('답변 연결이 끊겨 생성을 완료하지 못했습니다. 질문을 다시 시도해 주세요.');
            return terminal;
        } catch (error) {
            onProgress({ answerReset: true, streamFailed: true });
            throw error;
        } finally {
            options.signal?.removeEventListener('abort', aborted);
            await reader.cancel().catch(() => undefined);
            reader.releaseLock();
        }
    }

    /** Follow new text only while the reader stays at the bottom of the conversation. */
    public progressScroll(container: HTMLElement | null, message: any, snapshot: any): () => void {
        if (!container) return () => undefined;
        const position = container.scrollTop;
        const height = container.scrollHeight;
        const followBottom = container.scrollHeight - container.clientHeight - position < 64;
        const tracking = this.progressFollowing.get(container);
        // A changed position since our last update means the reader chose another location.
        if (tracking && Math.abs(position - tracking.top) > 2) tracking.following = followBottom;
        const follow = tracking ? tracking.following : followBottom;
        return () => {
            if (!container.isConnected || !this.isGenerating(message)) return;
            // A stage replacement can clamp scrollTop while the DOM is rendered.
            // Follow both new execution details and growing answer text.
            if (follow && (Math.abs(container.scrollTop - position) <= 2 || container.scrollHeight !== height)) {
                container.scrollTop = container.scrollHeight;
            }
            if (tracking) tracking.top = container.scrollTop;
        };
    }

    public isGenerating(message: any): boolean {
        return Boolean(message?.processing || message?.kind === 'progress' || message?.streaming);
    }

    /** Only the newest real execution event is visible while an answer is pending. */
    public currentProgress(message: any): any {
        if (!this.isGenerating(message)) return null;
        const progress = message?.activeExecutionProgress;
        if (!progress) return null;
        return message.progressNotice ? { ...progress, message: message.progressNotice } : progress;
    }

    private progressToolCategory(event: any): string {
        const categories = ['BANDI 데이터 조회', '문서 검색', 'AI 모델', '공정 데이터 검색',
            '통계 분석', '공정 이력 비교', '차트 구성', '보고서 작성', '공정 조건 분석'];
        if (categories.includes(event?.toolCategory)) return event.toolCategory;
        // Older server events carry a public detail label, never a raw Tool name.
        const legacyLabels: Record<string, string> = {
            '공정 데이터 조회': 'BANDI 데이터 조회', '데이터 목록 조회': 'BANDI 데이터 조회',
            '장비 목록 조회': 'BANDI 데이터 조회', '공정 목록 조회': 'BANDI 데이터 조회',
            '공정별 장비 조회': 'BANDI 데이터 조회', '공정 이력 조회': 'BANDI 데이터 조회',
            '측정 데이터 조회': 'BANDI 데이터 조회', '공정 파라미터 조회': 'BANDI 데이터 조회',
            '데이터 그룹 조회': 'BANDI 데이터 조회', '측정 항목 조회': 'BANDI 데이터 조회',
            '측정 단위 조회': 'BANDI 데이터 조회', '단위 분류 조회': 'BANDI 데이터 조회',
            'AI 모델 분석': 'AI 모델'
        };
        if (Object.prototype.hasOwnProperty.call(legacyLabels, event?.tool)) return legacyLabels[event.tool];
        return categories.includes(event?.tool) ? event.tool : '';
    }

    public applyProgress(message: any, snapshot: any) {
        if (!message) return;
        message.progressNotice = snapshot?.notice || '';
        if (snapshot?.answerReset) {
            message.content = '';
            message.streaming = false;
        }
        if (typeof snapshot?.answerDelta === 'string' && snapshot.answerDelta) {
            message.content = (message.streaming ? message.content || '' : '') + snapshot.answerDelta;
            message.streaming = true;
        }
        const states = ['running', 'completed', 'failed', 'retrying', 'no_data', 'skipped', 'fallback'];
        const events = new Map<number, any>((message.progressEvents || []).map((event: any) => [event.seq, event]));
        const cursor = Math.max(0, ...events.keys());
        const updates: any[] = [];
        for (const event of snapshot?.events || []) {
            if (!Number.isSafeInteger(event?.seq) || !states.includes(event?.status) || typeof event?.message !== 'string') continue;
            if (event.seq > cursor) updates.push(event);
            events.set(event.seq, event);
        }
        message.progressEvents = [...events.values()].sort((a, b) => a.seq - b.seq);
        updates.sort((a, b) => a.seq - b.seq);
        if (snapshot?.answerDelta) updates.push({ phase: 'answer', status: 'running', message: '답변을 작성하고 있습니다.' });
        if (snapshot?.streamFailed) updates.push({ phase: 'answer', status: 'failed', message: '답변을 완료하지 못했습니다.' });
        const groups = [message.generationProcess?.steps, message.processSteps, message.answerFlow?.steps].filter(Array.isArray);
        for (const event of updates) {
            const phase = event.phase;
            const status = event.status;
            if (['context', 'intent', 'parameters'].includes(phase)) message.activeExecutionProgress = null;
            else if (['tool', 'analysis', 'sources', 'answer', 'request'].includes(phase)) {
                message.activeExecutionProgress = {
                    phase, status,
                    stageLabel: ['analysis', 'sources', 'answer'].includes(phase)
                        ? (event.agentMode === 'data_search' ? '조회 결과 LLM 분석·요약'
                            : message.activeExecutionProgress?.stageLabel || '') : '',
                    toolCategory: phase === 'tool' ? this.progressToolCategory(event)
                        : message.activeExecutionProgress?.toolCategory || '',
                    message: phase === 'tool' && event.stageStart === true && status === 'running'
                        ? 'Tool 실행 시작' : event.message
                };
            }
            for (const steps of groups) {
                const tool = steps.find((step: any) => ['tools', 'execute'].includes(step.id));
                const answer = steps.find((step: any) => step.id === 'answer');
                if (answer && event.agentMode === 'data_search') answer.label = this.resultAnalysisStep(event).label;
                const update = (step: any, state: string, detail?: string) => {
                    if (step) Object.assign(step, { state, status: state, stateLabel: this.progressStatus(state), ...(detail ? { detail } : {}) });
                };
                const initialPhases = ['context', 'intent', 'parameters'];
                if (initialPhases.includes(phase)) {
                    const index = initialPhases.indexOf(phase);
                    const ids = [['context'], ['intent'], ['parameters', 'skill']];
                    ids.forEach((names, position) => {
                        const step = steps.find((item: any) => names.includes(item.id));
                        update(step, position < index ? 'completed' : position === index ? status : 'pending', position === index ? event.message : undefined);
                    });
                    update(tool, 'pending');
                    update(answer, 'pending');
                }
                // One nested/parallel Tool finishing does not complete the entire Tool stage.
                if (phase === 'tool') {
                    // A Tool event also confirms the prerequisite analysis has finished.
                    for (const id of ['context', 'intent', 'parameters', 'skill']) update(steps.find((step: any) => step.id === id), 'completed');
                    update(tool, ['completed', 'no_data', 'skipped', 'fallback'].includes(status) ? 'running' : status, event.message);
                }
                if (['analysis', 'sources', 'answer'].includes(phase)) {
                    if (!snapshot?.streamFailed) update(tool, 'completed');
                    // Analysis and source preparation belong to the final combined stage.
                    update(answer, status === 'completed' && phase !== 'answer' ? 'running' : status, event.message);
                }
            }
            if (message.answerFlow) message.answerFlow.currentStep = phase === 'tool' ? 'execute'
                : phase === 'parameters' ? 'skill' : ['context', 'intent'].includes(phase) ? phase
                : ['analysis', 'sources', 'answer'].includes(phase) ? 'answer' : message.answerFlow.currentStep;
        }
    }

    public progressStatus(status: string): string {
        return ({ running: '진행', completed: '완료', failed: '실패', retrying: '재시도', no_data: '결과 없음', skipped: '생략', fallback: '일부 결과' } as any)[status] || '대기';
    }

    public sourceSummary(sources: any[]): string {
        const used = sources.filter(source => source.usage === 'used');
        const documents = used.filter(source => this.isDocumentSource(source));
        const identity = (source: any) => source.documentId || String(source.title || source.label || '').replace(/\s*·?\s*p\.\d+.*$/, '');
        const count = new Set(documents.map(identity)).size;
        const pages = new Set(documents.filter(source => this.evidencePage(source)).map(source => `${identity(source)}:${this.evidencePage(source)}`)).size;
        const types = [...new Set(used.map(source => source.typeLabel || (this.isDocumentSource(source) ? '문서' : '데이터')))];
        const title = documents[0]?.title || documents[0]?.label || '';
        const references = sources.filter(source => source.usage !== 'used').length;
        return [`활용 문서 ${count}개`, `출처 유형: ${types.join(', ') || '활용 확인 없음'}`, title ? `대표: ${title}` : '', `인용 ${pages}페이지`, references ? `참고 ${references}건` : ''].filter(Boolean).join(' · ');
    }

    public executionTools(message: any): string {
        const labels: any = { 'data.search': '공정 데이터 조회', 'dataset.search': '데이터 목록 조회', 'process.search': '공정 데이터 검색', 'rag.search': '문서 검색', 'analytics.analyze': '통계 분석', 'trace.compare': '공정 이력 비교', 'model.predict': 'AI 모델 분석', 'chart.render': '차트 구성', 'report.compose': '보고서 작성', 'optimize.recommend': '공정 조건 분석',
            'serving.infer': 'LLM 해석·요약',
            'alsphere_authenticate': '알세미 인증',
            'alsphere_process_options': '알세미 공정·학습 설정 조회',
            'alsphere_catalog': '알세미 공정·모델 목록 조회',
            'alsphere_list_models': '알세미 모델 목록 조회',
            'alsphere_model_detail': '알세미 모델 입력 정보 조회',
            'alsphere_model_status': '알세미 학습 상태 확인',
            'alsphere_predict': '알세미 모델 예측',
            'alsphere_deposition_predict': '알세미 증착 예측',
            'alsphere_etch_predict': '알세미 식각 예측',
            'alsphere_photo_predict': '알세미 포토 예측',
            'alsphere_doping_predict': '알세미 이온주입 예측',
            'alsphere_upload_training_csv': '알세미 학습 CSV 업로드',
            'alsphere_training_file': '알세미 CSV 전처리 상태 확인',
            'alsphere_train_uploaded_data': '알세미 모델 학습'
        };
        const actions = message?.actions || message?.meta?.actions || message?.orchestration?.pipeline?.actions || [];
        return [...new Set(actions.filter((action: any) => ['completed', 'failed', 'error'].includes(action?.status)).map((action: any) =>
            `${labels[action.name] || '기타 도구'}${['failed', 'error'].includes(action.status) ? ' (실패)' : action.recoveryStatus === 'fallback_completed' ? ' (대체 처리)' : ''}`))].join(', ') || '실행 없음';
    }

    public executionTime(message: any): string {
        const elapsed = message?.elapsedMs ?? message?.meta?.elapsedMs;
        return typeof elapsed === 'number' && Number.isFinite(elapsed) && elapsed >= 0 ? `${(elapsed / 1000).toFixed(1)}초` : '';
    }

    public dataEvidenceFields(source: any): any[] {
        if (!source?.fields || typeof source.fields !== 'object') return [];
        if (Array.isArray(source.fields)) return source.fields;
        const labels: any = { returnedRows: '조회 건수', total: '전체 건수', page: '페이지', scope: '분석 범위' };
        return Object.entries(source.fields).filter(([key, value]) => !/service.?key|api.?key|token|password|secret|authorization/i.test(key)
            && value !== null && ['string', 'number', 'boolean'].includes(typeof value))
            .slice(0, 20).map(([key, value]) => ({ label: labels[key] || key, value: value === 'returned_rows_only' ? '조회된 데이터만' : value }));
    }

    public hasAnswerTable(message: any): boolean {
        return this.markdown(message?.content || message?.answer || '').includes('<table>');
    }

    public answerWarnings(message: any): string[] {
        const warnings: string[] = [];
        const llm = message?.llm || {};
        const synthesisFailed = llm.synthesisStatus === 'failed' || llm.fallback
            || ['failed', 'error', 'fallback'].includes(String(llm.status || '').toLowerCase());
        if (synthesisFailed) warnings.push('LLM 분석을 완료하지 못했습니다. 답변의 제한사항을 확인해 주세요.');
        if ((message?.actions || []).some((action: any) => action.status === 'failed' || action.recoveryStatus === 'fallback_completed')) warnings.push('일부 Tool 실행에 실패하거나 대체 결과를 사용했습니다.');
        const catalog = this.normalizeRetrieval(message?.retrieval)?.catalogSummary;
        const mainPageGuidance = llm.answerMode === 'main_page_guidance'
            && llm.synthesisStatus === 'completed' && llm.synthesisVerified === true
            && !synthesisFailed && !llm.degraded
            && (message?.content || message?.answer) === '메인 페이지에서 확인하세요.';
        if (!mainPageGuidance && (message?.retrieval?.pagination?.truncated || catalog?.availableTotal > 5)) {
            warnings.push('현재 답변에는 일부만 반환했으며, 메인 페이지를 통해 전체 결과를 알 수 있습니다.');
        }
        // Public codes are translated locally; provider text never becomes a warning.
        const ragMessages: Record<string, string> = {
            retrieval_unavailable: '문서 검색을 완료하지 못했습니다. 문서 연결 상태를 확인한 뒤 다시 시도해 주세요.',
            keyword_fallback: '의미 검색을 완료하지 못해 키워드 검색 결과를 사용했습니다. 근거를 다시 확인해 주세요.',
            retrieval_fallback: '문서 검색의 대체 경로를 사용했습니다. 검색 범위와 근거를 확인해 주세요.',
            rerank_fallback: '문서 재순위화를 완료하지 못해 기존 검색 순서를 사용했습니다.',
            rerank_provider_fallback: '문서 재순위화에 보조 제공자를 사용했습니다.',
            numeric_source_review: '수치 조건은 원문의 항목·값·단위를 확인한 뒤 판단해 주세요.',
            comparison_incomplete: '일부 비교 대상의 문서 근거가 부족합니다. 확인된 대상만 설명할 수 있습니다.'
        };
        const ragCodes = message?.answerQuality?.ragWarningCodes;
        if (Array.isArray(ragCodes)) {
            for (const code of new Set(ragCodes.slice(0, 32))) {
                if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(ragMessages, code)) {
                    warnings.push(ragMessages[code]);
                }
            }
        }
        return warnings;
    }

    /** Resolve an explicit citation page. Array order is never a page number. */
    public evidencePage(source: any): number | null {
        const positive = (value: any) => {
            if (typeof value !== 'number' && typeof value !== 'string') return null;
            if (!/^\d+$/.test(String(value).trim())) return null;
            const page = Number(value);
            return Number.isSafeInteger(page) && page > 0 ? page : null;
        };
        const fields = Array.isArray(source?.fields)
            ? source.fields.find((field: any) => /^(page|페이지)$/i.test(String(field?.key || field?.label || '')))?.value
            : source?.fields?.page;
        for (const value of [source?.page, fields]) {
            const page = positive(value);
            if (page !== null) return page;
        }
        for (const value of [typeof source === 'string' ? source : '', source?.subtitle, source?.detail, source?.label]) {
            const match = String(value || '').match(/(?:\bp\.\s*|\bpage\s+)(\d+)\b|(?:^|\s)(\d+)\s*페이지/i);
            const page = match && positive(match[1] || match[2]);
            if (page) return page;
        }
        return null;
    }

    /** A citation may reuse a known document, but never a chunk from a different page. */
    public citationSource(citation: any, evidence: any[]): any {
        const text = String(citation || '').trim();
        const title = text.split(/\s+(?:p\.\s*\d+|page\s+\d+)/i)[0].trim();
        const page = this.evidencePage(text);
        const candidates = (evidence || []).filter((item: any) =>
            [item?.title, item?.label, item?.id, item?.documentId].some(value => String(value || '').trim() === title));
        const matching = candidates.find((item: any) => page !== null && this.evidencePage(item) === page);
        const documentIds = [...new Set(candidates.map((item: any) => item?.documentId).filter(Boolean))];
        const source = matching || (documentIds.length === 1 ? candidates.find((item: any) => item.documentId === documentIds[0]) : null);
        return {
            label: page ? `${title} · p.${page}` : text, page,
            detailApi: source?.detailApi === 'knowledge_document_evidence' ? source.detailApi : '',
            documentId: String(source?.documentId || ''),
            chunkId: matching ? String(matching.chunkId || '') : '',
        };
    }

    /** Accept only the server-rendered PNG for this explicit source page. */
    public evidenceImage(preview: any, source: any): any {
        const page = this.evidencePage({ page: preview?.page });
        const expected = source?.chunkId ? null : this.evidencePage(source);
        const dataUrl = String(preview?.dataUrl || '');
        const width = Number(preview?.width);
        const height = Number(preview?.height);
        const pageCount = this.evidencePage({ page: preview?.pageCount });
        if (!page || (expected !== null && page !== expected)
            || (preview?.documentId && String(preview.documentId) !== String(source?.documentId))
            || !/^data:image\/png;base64,iVBORw0KGgo[A-Za-z0-9+/]*={0,2}$/.test(dataUrl)
            || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0
            || (pageCount !== null && page > pageCount)) {
            throw new Error('해당 근거 페이지의 원본 PDF 이미지를 확인할 수 없습니다.');
        }
        return { page, pageCount, dataUrl, width, height, zoom: 100, title: String(preview?.title || source?.label || source?.title || '원본 PDF') };
    }

    public evidencePreviewParams(source: any): any {
        if (source?.detailApi !== 'knowledge_document_evidence' || !source?.documentId) {
            throw new Error('이 근거에는 원본 PDF 연결 정보가 없습니다.');
        }
        if (source.chunkId) return { document_id: source.documentId, chunk_id: source.chunkId };
        const page = this.evidencePage(source);
        if (!page) throw new Error('근거 페이지 번호가 없어 원본 PDF 이미지를 표시할 수 없습니다.');
        return { document_id: source.documentId, page };
    }

    public isDocumentSource(source: any): boolean {
        return Boolean(source?.documentSource || source?.documentId || source?.detailApi === 'knowledge_document_evidence'
            || ['document_chunk', 'citation'].includes(source?.type) || source?.citation);
    }

    public zoomEvidenceImage(image: any, change: number) {
        if (image) image.zoom = Math.max(100, Math.min(300, Number(image.zoom || 100) + change));
    }

    public isDataSearch(message: any): boolean {
        const meta = message?.meta && !Array.isArray(message.meta) ? message.meta : {};
        const mode = String(message?.resolvedAgentMode || message?.analysis?.resolvedAgentMode || meta.resolvedAgentMode || meta.analysis?.resolvedAgentMode
            || message?.agentMode || message?.analysis?.agentMode || meta.agentMode || meta.analysis?.agentMode || '');
        if (mode && mode !== 'auto') return mode === 'data_search';
        return (message?.retrieval || meta.retrieval)?.kind === 'data-search'
            || String(message?.intent || meta.intent || '') === 'search';
    }

    public normalizeRetrieval(value: any): any {
        if (!value || value.kind !== 'data-search') return null;
        const scalar = (text: any) => typeof text === 'string' ? text.slice(0, 5000) : '';
        const conditions = (Array.isArray(value.conditions) ? value.conditions : [])
            .filter((item: any) => item && !/service.?key|api.?key|token|password|secret|authorization/i.test(String(item.key || '') + String(item.label || '')))
            .slice(0, 32).map((item: any) => ({ key: scalar(item.key).slice(0, 80), label: scalar(item.label).slice(0, 120), value: scalar(item.value) }))
            .filter((item: any) => item.label && item.value);
        const pagination: any = {};
        for (const key of ['page', 'numOfRows', 'returnedRows', 'total', 'from', 'to']) {
            const number = value.pagination?.[key];
            pagination[key] = typeof number === 'number' && Number.isSafeInteger(number) && number >= (['page', 'numOfRows'].includes(key) ? 1 : 0) ? number : null;
        }
        pagination.truncated = value.pagination?.truncated === true;
        if (value.pagination?.scope === 'combined-first-pages') {
            pagination.scope = 'combined-first-pages';
            pagination.pages = (Array.isArray(value.pagination.pages) ? value.pagination.pages : []).slice(0, 2).map((row: any) => {
                const page: any = {};
                for (const key of ['page', 'numOfRows', 'returnedRows', 'total']) {
                    const number = row?.[key];
                    page[key] = typeof number === 'number' && Number.isSafeInteger(number) && number >= (['page', 'numOfRows'].includes(key) ? 1 : 0) ? number : null;
                }
                return page;
            });
        }
        const timestamp = typeof value.retrievedAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value.retrievedAt)
            && /(?:Z|[+-]\d{2}:\d{2})$/.test(value.retrievedAt) && Number.isFinite(Date.parse(value.retrievedAt)) ? value.retrievedAt : null;
        const summary = value.catalogSummary;
        const catalogSummary = summary && summary.complete === true
            && ['equipment', 'process', 'processes', 'process_equipment'].includes(summary.kind)
            && ['search_data', 'lot_processes'].includes(summary.basis)
            && typeof summary.catalogTotal === 'number' && Number.isSafeInteger(summary.catalogTotal) && summary.catalogTotal >= 0
            && typeof summary.availableTotal === 'number' && Number.isSafeInteger(summary.availableTotal)
            && summary.availableTotal >= 0 && summary.availableTotal <= summary.catalogTotal
            ? { kind: summary.kind, catalogTotal: summary.catalogTotal, availableTotal: summary.availableTotal, basis: summary.basis, complete: true }
            : null;
        return { kind: 'data-search', status: ['completed', 'no_data', 'unavailable'].includes(value.status) ? value.status : 'unavailable', conditions,
            retrievedAt: timestamp, pagination: value.pagination && typeof value.pagination === 'object' ? pagination : null, catalogSummary };
    }

    public retrievalRows(message: any): any[] {
        if (!this.isDataSearch(message)) return [];
        const info = this.normalizeRetrieval(message?.retrieval || message?.meta?.retrieval);
        const page = info?.pagination;
        const pageParts = page?.scope === 'combined-first-pages' ? [
            ...(page.pages || []).map((row: any, index: number) => `조회 ${index + 1}: ${row.page === null ? '페이지 미제공' : `${row.page}페이지`}${row.returnedRows === null ? '' : `, 반환 ${row.returnedRows}건`}${row.total === null ? '' : ` / 전체 ${row.total}건`}`),
            page.returnedRows === null ? '' : `반환 합계 ${page.returnedRows}건`, page.truncated ? '일부 결과' : ''
        ].filter(Boolean) : page ? [
            page.page === null ? '' : `${page.page}페이지`,
            page.numOfRows === null ? '' : `페이지당 ${page.numOfRows}건`,
            page.returnedRows === null ? '' : `반환 ${page.returnedRows}건`,
            page.from === null || page.to === null ? '' : `범위 ${page.from}–${page.to}`,
            page.total === null ? '' : `전체 ${page.total}건`,
            page.truncated ? '일부 결과' : ''
        ].filter(Boolean) : [];
        return [
            { label: '조회 조건', value: info?.conditions.length ? info.conditions.map((item: any) => `${item.label}: ${item.value}`).join(' · ')
                : info && ['completed', 'no_data'].includes(info.status) ? '추가 조건 없음' : '조회 조건 미제공' },
            { label: '조회 시각', value: info?.retrievedAt || '조회 시각 미제공' },
            { label: '페이지 정보', value: pageParts.length ? pageParts.join(' · ') : '페이지 정보 미제공' }
        ];
    }

    private escape(value: any): string {
        return String(value ?? '').replace(/[&<>"']/g, character => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[character] || character));
    }

    private href(value: string): string {
        const raw = String(value || '').trim();
        if (!raw || /[\u0000-\u0020\u007f\\"'<>]/.test(raw) || raw.startsWith('//')) return '';
        if (!/^https?:\/\//i.test(raw) && !/^\/(?!\/)/.test(raw) && !/^#[\w.-]+$/.test(raw)) return '';
        try {
            const url = new URL(raw, 'https://chat.invalid');
            if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return '';
            let secret = false;
            url.searchParams.forEach((_value, key) => {
                if (/^(?:servicekey|apikey|token|accesstoken|authorization|password|secret|signature)$/i.test(key.replace(/[_-]/g, ''))) secret = true;
            });
            return secret ? '' : raw;
        } catch (_) { return ''; }
    }

    private inline(value: string, depth: number = 0): string {
        if (depth > 8) return this.escape(value);
        let output = '';
        let index = 0;
        while (index < value.length) {
            const rest = value.slice(index);
            const escaped = rest.match(/^\\([\\`*_{}\[\]()#+\-.!|>])/);
            if (escaped) {
                output += this.escape(escaped[1]);
                index += escaped[0].length;
                continue;
            }
            const code = rest.match(/^(`+)([^\n]*?)\1(?!`)/);
            if (code && code[2]) {
                output += `<code>${this.escape(code[2])}</code>`;
                index += code[0].length;
                continue;
            }
            const link = rest.match(/^(!?)\[([^\]\n]+)\]\(/);
            if (link) {
                let cursor = link[0].length;
                let level = 1;
                for (; cursor < rest.length && level; cursor++) {
                    if (rest[cursor] === '(') level++;
                    else if (rest[cursor] === ')') level--;
                    else if (rest[cursor] === '\n') break;
                }
                if (!level) {
                    const label = this.inline(link[2], depth + 1);
                    const url = this.href(rest.slice(link[0].length, cursor - 1));
                    output += url && !link[1]
                        ? `<a href="${this.escape(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`
                        : label;
                    index += cursor;
                    continue;
                }
            }
            let matched = false;
            for (const delimiter of ['**', '__', '*', '_']) {
                if (!rest.startsWith(delimiter) || (delimiter.includes('_') && /[\p{L}\p{N}]/u.test(value[index - 1] || ''))) continue;
                const end = value.indexOf(delimiter, index + delimiter.length);
                if (end <= index + delimiter.length) continue;
                const body = value.slice(index + delimiter.length, end);
                if (/^\s|\s$/.test(body)) continue;
                const tag = delimiter.length === 2 ? 'strong' : 'em';
                output += `<${tag}>${this.inline(body, depth + 1)}</${tag}>`;
                index = end + delimiter.length;
                matched = true;
                break;
            }
            if (matched) continue;
            output += value[index] === '\n' ? '<br>' : this.escape(value[index]);
            index++;
        }
        return output;
    }

    private tableCells(value: string): string[] {
        const text = value.trim().replace(/^\|/, '').replace(/(?<!\\)\|$/, '');
        const cells: string[] = [];
        let current = '';
        let code = false;
        for (let index = 0; index < text.length; index++) {
            const character = text[index];
            if (character === '\\' && index + 1 < text.length) {
                current += character + text[++index];
            } else if (character === '`') {
                code = !code;
                current += character;
            } else if (character === '|' && !code) {
                cells.push(current.trim());
                current = '';
            } else current += character;
        }
        cells.push(current.trim());
        return cells;
    }

    private divider(value: string): boolean {
        return this.tableCells(value).every(cell => /^:?-{3,}:?$/.test(cell));
    }

    public markdown(value: any, depth: number = 0): string {
        const lines = String(value ?? '').replace(/\r\n?/g, '\n').split('\n');
        if (depth > 8) return `<p>${this.escape(lines.join('\n'))}</p>`;
        const output: string[] = [];
        let paragraph: string[] = [];
        const flush = () => {
            if (paragraph.length) output.push(`<p>${this.inline(paragraph.join('\n'))}</p>`);
            paragraph = [];
        };
        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const trimmed = line.trim();
            if (!trimmed) { flush(); continue; }
            const fence = trimmed.match(/^(`{3,}|~{3,})[^`~]*$/);
            if (fence) {
                flush();
                const code: string[] = [];
                const closing = new RegExp(`^${fence[1][0]}{${fence[1].length},}\\s*$`);
                index++;
                while (index < lines.length && !closing.test(lines[index].trim())) code.push(lines[index++]);
                output.push(`<pre><code>${this.escape(code.join('\n'))}</code></pre>`);
                continue;
            }
            const heading = trimmed.match(/^(#{1,6})\s+(.+?)(?:\s+#+)?$/);
            const emphasized = trimmed.match(/^(?:\*\*(.+)\*\*|__(.+)__)$/);
            if (heading || emphasized) {
                flush();
                const level = heading ? heading[1].length : 4;
                const text = heading ? heading[2] : (emphasized![1] || emphasized![2]);
                output.push(`<h${level}>${this.inline(text)}</h${level}>`);
                continue;
            }
            if (line.includes('|') && index + 1 < lines.length && this.divider(lines[index + 1])) {
                flush();
                const headers = this.tableCells(line);
                const rows: string[][] = [];
                index += 2;
                while (index < lines.length && lines[index].includes('|') && lines[index].trim()) rows.push(this.tableCells(lines[index++]));
                index--;
                const width = Math.max(headers.length, ...rows.map(row => row.length));
                const renderCells = (row: string[], tag: string) => Array.from({ length: width }, (_, column) => `<${tag}>${this.inline(row[column] || '')}</${tag}>`).join('');
                output.push(`<div class="chat-markdown-table"><table><thead><tr>${renderCells(headers, 'th')}</tr></thead><tbody>${rows.map(row => `<tr>${renderCells(row, 'td')}</tr>`).join('')}</tbody></table></div>`);
                continue;
            }
            const list = trimmed.match(/^(?:([-*+])|(\d+)[.)])\s+(.+)$/);
            if (list) {
                flush();
                const ordered = Boolean(list[2]);
                const items: string[] = [];
                const pattern = ordered ? /^\s*\d+[.)]\s+(.+)$/ : /^\s*[-*+]\s+(.+)$/;
                while (index < lines.length) {
                    const item = lines[index].match(pattern);
                    if (!item) break;
                    items.push(`<li>${this.inline(item[1])}</li>`);
                    index++;
                }
                index--;
                const tag = ordered ? 'ol' : 'ul';
                const start = ordered && Number(list[2]) > 1 ? ` start="${Math.min(Number(list[2]), 1000000)}"` : '';
                output.push(`<${tag}${start}>${items.join('')}</${tag}>`);
                continue;
            }
            if (/^>\s?/.test(trimmed)) {
                flush();
                const quote: string[] = [];
                while (index < lines.length && /^\s*>/.test(lines[index])) quote.push(lines[index++].replace(/^\s*>\s?/, ''));
                index--;
                output.push(`<blockquote>${this.markdown(quote.join('\n'), depth + 1)}</blockquote>`);
                continue;
            }
            if (/^([-*_])(?:\s*\1){2,}\s*$/.test(trimmed)) { flush(); output.push('<hr>'); continue; }
            paragraph.push(trimmed);
        }
        flush();
        return output.join('');
    }

    private label(value: any): string {
        return typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 160) : '';
    }

    public attribution(message: any): { llm: string; agents: string[] } {
        const meta = message?.meta && typeof message.meta === 'object' ? message.meta : {};
        const llm = message?.llm ?? meta.llm;
        const orchestration = message?.orchestration ?? meta.orchestration ?? {};
        const completed = (item: any) => ['completed', 'done', 'ok', 'success'].includes(String(item?.status || '').toLowerCase())
            && !item?.fallback && !item?.degraded && item?.recoveryStatus !== 'fallback_completed';
        let modelLabel = '확인 불가';
        if (llm && typeof llm === 'object') {
            const model = llm.model;
            const name = typeof model === 'string' ? this.label(model) : this.label(model?.upstreamModel)
                || this.label(model?.name) || this.label(model?.id) || this.label(model?.model);
            const status = String(llm.status || '').toLowerCase();
            if (llm.fallback || llm.degraded) modelLabel = '대체 응답 (LLM 확인 불가)';
            else if (['failed', 'error', 'blocked'].includes(status) || llm.error) modelLabel = '응답 생성 실패';
            else if (['skipped', 'not_executed', 'not_requested'].includes(status)) modelLabel = '미사용';
            else if (name) modelLabel = completed(llm) || llm.synthesisVerified === true
                ? name : `${name} (실행 상태 확인 불가)`;
        }
        const actionValue = message?.actions ?? meta.actions ?? orchestration?.pipeline?.actions;
        const actions: any[] = Array.isArray(actionValue) ? actionValue : [];
        const runValues = [message?.agentRuns, meta.agentRuns, orchestration.agentRuns, orchestration.domainOutputs];
        const runs = runValues.flatMap(value => Array.isArray(value) ? value : []);
        const agents: string[] = [];
        const add = (value: any) => { const name = this.label(value); if (name && !agents.includes(name)) agents.push(name); };
        const domains = runs.filter(run => run?.group === 'domain_expert' || /_agent$/.test(String(run?.id || '')));
        const successfulDomains = domains.filter(run => {
            if (!completed(run)) return false;
            const relevant = actions.filter(action => Array.isArray(run.tools) && run.tools.includes(action?.name));
            return !relevant.length || relevant.some(completed);
        });
        successfulDomains.forEach(run => add(run.name || run.id));
        const toolLabels: { [key: string]: string } = {
            'data.search': 'Data Search Agent', 'dataset.search': 'Data Search Agent',
            'process.search': 'Data Search Agent', 'semiconductor.data': 'Data Search Agent',
            'rag.search': 'RAG Agent', 'model.predict': 'AI Model Agent',
            'serving.infer': '응답 생성 Agent',
            'trace.compare': 'Trace Analysis Agent', 'optimize.recommend': 'SPC Agent',
            'analytics.analyze': '분석 도구', 'chart.render': '시각화 도구', 'report.compose': '보고서 도구',
        };
        actions.filter(completed).forEach(action => {
            if (successfulDomains.some(run => Array.isArray(run.tools) && run.tools.includes(action.name))) return;
            add(toolLabels[action.name] || action.name);
        });
        if (!agents.length) runs.filter(completed).filter(run => run.group === 'core').forEach(run => add(run.name || run.id));
        if (!agents.length) {
            const failures = actions.some(action => ['failed', 'error', 'blocked'].includes(String(action?.status || '').toLowerCase()))
                || runs.some(run => ['failed', 'error', 'blocked'].includes(String(run?.status || '').toLowerCase()));
            const fallback = actions.some(action => action?.fallback || action?.recoveryStatus === 'fallback_completed');
            agents.push(failures ? '실행 실패' : fallback ? '대체 처리 (Agent 확인 불가)'
                : (Array.isArray(actionValue) || runValues.some(Array.isArray)) ? '사용 없음' : '확인 불가');
        }
        return { llm: modelLabel, agents };
    }
}
